import {
  AlertCircle,
  ChevronRight,
  ClipboardList,
  FileText,
  Receipt,
  ScrollText,
  Shield,
} from "lucide-react";
import { useState } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import type { AttentionItem, AttentionKind, AttentionUrgency } from "@/hooks/use-project-attention";
import { cn } from "@/lib/utils";

interface ActionQueueCardProps {
  items: AttentionItem[] | undefined;
  isLoading: boolean;
}

const KIND_ICONS: Record<AttentionKind, typeof FileText> = {
  compliance: Shield,
  rfi: FileText,
  payapp: Receipt,
  pco: ScrollText,
  submittal: ClipboardList,
};

const URGENCY_DOT: Record<AttentionUrgency, string> = {
  block: "bg-rose-500",
  overdue: "bg-amber-500",
  review: "bg-blue-500",
};

const URGENCY_LABEL: Record<AttentionUrgency, string> = {
  block: "Blocking",
  overdue: "Overdue",
  review: "For review",
};

type Filter = "all" | AttentionUrgency;

export function ActionQueueCard({ items, isLoading }: ActionQueueCardProps) {
  const [filter, setFilter] = useState<Filter>("all");
  const safeItems = items ?? [];

  const counts = {
    all: safeItems.length,
    block: safeItems.filter((i) => i.urgency === "block").length,
    overdue: safeItems.filter((i) => i.urgency === "overdue").length,
    review: safeItems.filter((i) => i.urgency === "review").length,
  };
  const filtered = filter === "all" ? safeItems : safeItems.filter((i) => i.urgency === filter);

  const tabs: Array<{ id: Filter; label: string; count: number }> = [
    { id: "all", label: "All", count: counts.all },
    { id: "block", label: "Blocking", count: counts.block },
    { id: "overdue", label: "Overdue", count: counts.overdue },
    { id: "review", label: "For review", count: counts.review },
  ];

  return (
    <div className="rounded-xl border border-border bg-card shadow-sm overflow-hidden">
      <div className="px-6 pt-5 pb-3 flex items-start justify-between gap-4">
        <div>
          <h2 className="text-[15px] font-semibold">Needs your attention</h2>
          <p className="text-[12.5px] text-muted-foreground mt-0.5">
            {counts.block + counts.overdue} items blocking or late · {counts.review} awaiting review
          </p>
        </div>
        <div className="flex items-center gap-1 flex-wrap">
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setFilter(t.id)}
              className={cn(
                "h-7 px-2.5 rounded-md text-[12px] font-medium inline-flex items-center gap-1.5 transition-colors",
                filter === t.id
                  ? "bg-secondary text-foreground"
                  : "text-muted-foreground hover:bg-secondary/60",
              )}
            >
              {t.label} <span className="tabular-nums text-muted-foreground">{t.count}</span>
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <div className="px-6 pb-5 space-y-3">
          {["a", "b", "c", "d"].map((k) => (
            <Skeleton key={k} className="h-12 w-full" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="px-6 py-10 text-center text-sm text-muted-foreground">
          <AlertCircle className="mx-auto mb-2 h-6 w-6 text-emerald-500/70" />
          All clear in this view.
        </div>
      ) : (
        <ul className="divide-y divide-border border-t border-border">
          {filtered.map((it) => {
            const Icon = KIND_ICONS[it.kind] ?? AlertCircle;
            return (
              <li key={it.id}>
                <div className="px-6 py-3.5 flex items-center gap-4">
                  <span
                    className={cn("h-1.5 w-1.5 rounded-full shrink-0", URGENCY_DOT[it.urgency])}
                  />
                  <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-[13.5px] font-medium text-foreground truncate">
                      {it.title}
                    </div>
                    <div className="text-[11.5px] text-muted-foreground mt-0.5 truncate">
                      {it.module} · {it.meta}
                    </div>
                  </div>
                  <div className="text-[11.5px] text-muted-foreground tabular-nums shrink-0">
                    {URGENCY_LABEL[it.urgency]} · {it.age}
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground/40 shrink-0" />
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
